import React, { useCallback, useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import pa from '../ProductActivityPage.module.css'
import { apiJson } from '../../lib/api'
import { useLayer } from '../../components/layers'
import { PendingButton } from '../../components/pending'
import { dayLabel } from '../productActivity/model'

// Weighing conflicts (D7.8 D). A Crew weighing that would change a weight a bin already carries, or that reaches a
// closed or locked day, is held, never applied and never thrown away. Here a person sees both readings and decides,
// once, with a reason: keep what the bins carry now, or use the held weighing. Using it is the only way a Control
// weight or an earlier weighing is replaced. A locked day refuses it (unlock first); keeping the current weight moves
// nothing, so a locked day allows it. Reading needs Batches read; deciding needs Batches edit.

export interface ConflictBin {
  batch_id: string
  bin: string
  tare_kg: number
  estimate_kg: number | null
  held_share_kg: number | null
  current: { weighed_kg: number | null; source: string | null; by: string | null; at: string | null; weighing_id: string | null }
}

export interface Conflict {
  kind: 'weighing_conflict'
  id: string
  resolved: boolean
  title: string
  summary: string
  reasons: string[]
  held: {
    producer_id: string
    device_event_id: string
    operator: string
    device_label: string | null
    captured_at: string | null
    arrived_at: string | null
    gross_kg: number
    tare_total_kg: number
    net_kg: number
    tare_source: string
    has_photo: boolean
  }
  against: { bins: { batch_id: string; bin: string; weighed_kg: number | null; weighed_source: string | null }[]; shut_days: { date: string; state: 'CLOSED' | 'LOCKED' }[] }
  bins: ConflictBin[]
  resolution: { id: string; decision: 'kept_current' | 'used_new'; actor: string; reason: string; decided_at: string; weighing_id: string | null } | null
}

export const CONFLICTS_PATH = '/api/batches/weighing-conflicts'
export const CONFLICT_LOCKED_TEXT = 'This day is locked. Unlock it first, then use the new weighing. Nothing was changed.'

const REASON_TEXT: Record<string, string> = {
  already_weighed: 'A bin on it already carries a weight, from Control or from an earlier weighing.',
  day_shut: 'It reaches a closed or locked day.',
}

const stamp = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
}
const kg = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${n.toFixed(1)} kg`)

const decisionLabel = (d: 'kept_current' | 'used_new') => (d === 'used_new' ? 'New weighing used' : 'Current weight kept')

interface Props {
  user: { getIdToken: () => Promise<string> } | null
  // Batches edit: without it everything here can be read and nothing decided.
  canEdit: boolean
  // Opens straight on this conflict (a link from Late data).
  initialId?: string | null
  onClose: () => void
  // Told after a decision so the page refreshes its batches and the count.
  onChanged: () => void
  toast: (message: string) => void
}

export function WeighingConflicts({ user, canEdit, initialId = null, onClose, onChanged, toast }: Props) {
  const [state, setState] = useState<'open' | 'resolved'>('open')
  const [items, setItems] = useState<Conflict[] | null>(null)
  const [error, setError] = useState('')
  const [openId, setOpenId] = useState<string | null>(initialId)
  const [busy, setBusy] = useState(false)
  const el = useRef<HTMLDivElement>(null)
  const seq = useRef(0)
  const { requestClose, confirm } = useLayer(true, onClose, { busy, el, modal: true })

  const load = useCallback(async () => {
    const n = ++seq.current
    try {
      const r = await apiJson<{ items: Conflict[] }>(user, state === 'resolved' ? `${CONFLICTS_PATH}?state=resolved` : CONFLICTS_PATH)
      if (n === seq.current) {
        setItems(r.items)
        setError('')
      }
    } catch (e: any) {
      if (n === seq.current) setError(e?.message || 'Could not load weighing conflicts')
    }
  }, [user, state])
  useEffect(() => {
    setItems(null)
    load()
  }, [load])

  return (
    <div className={o.drawerScrim} onClick={requestClose}>
      <div ref={el} className={o.drawer} role="dialog" aria-label="Weighing conflicts" data-testid="conflicts-drawer" onClick={(e) => e.stopPropagation()}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h3 className={o.drawerTitle}>Weighing conflicts</h3>
            <p className={o.drawerSub}>Crew weighings held for a person to decide. Nothing here has changed a weight.</p>
          </div>
          <button type="button" className={o.close} onClick={requestClose} title="Close" aria-label="Close" disabled={busy}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {openId ? (
          <ConflictDetail
            key={openId}
            user={user}
            id={openId}
            canEdit={canEdit}
            onBack={() => {
              setOpenId(null)
              load()
            }}
            onBusy={setBusy}
            onDecided={(message) => {
              toast(message)
              load()
              onChanged()
            }}
          />
        ) : (
          <div className={o.drawerBody}>
            <div style={{ display: 'flex', gap: '0.375rem', paddingBottom: '0.75rem', borderBottom: '1px solid var(--dp-line-soft)' }} role="group" aria-label="Weighing conflicts view">
              <button type="button" style={{ padding: '0.5rem 1rem', borderRadius: '999px', border: 'none', background: state === 'open' ? 'var(--dp-accent)' : 'transparent', color: state === 'open' ? '#fff' : 'var(--dp-ink2)', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: state === 'open' ? '700' : '600' }} aria-pressed={state === 'open'} onClick={() => setState('open')}>
                Open
              </button>
              <button type="button" style={{ padding: '0.5rem 1rem', borderRadius: '999px', border: 'none', background: state === 'resolved' ? 'var(--dp-accent)' : 'transparent', color: state === 'resolved' ? '#fff' : 'var(--dp-ink2)', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: state === 'resolved' ? '700' : '600' }} aria-pressed={state === 'resolved'} onClick={() => setState('resolved')}>
                Resolved
              </button>
            </div>
            {error && (
              <div style={{ padding: '0.75rem 1rem', background: 'var(--dp-bad-bg)', color: 'var(--dp-bad-fg)', borderRadius: 'var(--dp-radius-inset)', fontSize: '0.8125rem' }} role="alert">
                <span>{error}</span>
              </div>
            )}
            {items === null && !error ? (
              <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.8125rem', color: 'var(--dp-muted)' }}>Loading weighing conflicts…</div>
            ) : items && !items.length ? (
              <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.8125rem', color: 'var(--dp-muted)' }} data-testid="conflicts-empty">
                {state === 'open' ? 'No weighing is waiting for a decision.' : 'No weighing conflict has been decided yet.'}
              </div>
            ) : (
              <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', listStyle: 'none', padding: 0, margin: 0 }} aria-label={state === 'open' ? 'Open weighing conflicts' : 'Resolved weighing conflicts'}>
                {(items || []).map((c) => (
                  <li key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem', border: '1px solid var(--dp-line)', borderRadius: 'var(--dp-radius-inset)', background: 'var(--dp-surface)' }} data-testid="conflict-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <b style={{ fontSize: '0.8125rem', fontWeight: '700', color: 'var(--dp-ink)' }} id={`conflict-title-${c.id}`}>{c.title}</b>
                        {c.resolution && <span style={{ fontSize: '0.6875rem', fontWeight: '700', textTransform: 'uppercase', padding: '0.25rem 0.625rem', borderRadius: '999px', background: 'var(--dp-accent)', color: '#fff', whiteSpace: 'nowrap' }}>{decisionLabel(c.resolution.decision)}</span>}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--dp-ink2)', marginBottom: '0.5rem' }}>{c.summary}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--dp-muted)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <span>{c.held.operator}</span>
                        <span>captured {stamp(c.held.captured_at)}</span>
                        <span>arrived {stamp(c.held.arrived_at)}</span>
                        {c.against.shut_days.some((d) => d.state === 'LOCKED') && <span style={{ color: '#b4452f', fontWeight: '600' }}>Locked day</span>}
                      </div>
                    </div>
                    <button type="button" className={o.ghost} onClick={() => setOpenId(c.id)} aria-describedby={`conflict-title-${c.id}`} style={{ flex: 'none' }}>
                      {state === 'open' && canEdit ? 'Review' : 'View'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className={o.drawerFoot}>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Close
          </button>
        </div>
      </div>
      {confirm}
    </div>
  )
}

function ConflictDetail({
  user,
  id,
  canEdit,
  onBack,
  onBusy,
  onDecided,
}: {
  user: Props['user']
  id: string
  canEdit: boolean
  onBack: () => void
  onBusy: (b: boolean) => void
  onDecided: (message: string) => void
}) {
  const [c, setC] = useState<Conflict | null>(null)
  const [loadErr, setLoadErr] = useState('')
  const [deciding, setDeciding] = useState<null | 'kept_current' | 'used_new'>(null)
  const seq = useRef(0)

  const load = useCallback(async () => {
    const n = ++seq.current
    try {
      const r = await apiJson<Conflict>(user, `${CONFLICTS_PATH}/${id}`)
      if (n === seq.current) {
        setC(r)
        setLoadErr('')
      }
    } catch (e: any) {
      if (n === seq.current) setLoadErr(e?.message || 'Could not load this weighing')
    }
  }, [user, id])
  useEffect(() => {
    load()
  }, [load])

  if (!c) {
    return (
      <div className={pa.drawerBody}>
        <button type="button" className={pa.ghostBtn} onClick={onBack}>
          ← All weighing conflicts
        </button>
        {loadErr ? (
          <div className={pa.errorBar} role="alert">
            <span>{loadErr}</span>
          </div>
        ) : (
          <div className={pa.empty}>Loading this weighing…</div>
        )}
      </div>
    )
  }

  const locked = c.against.shut_days.filter((d) => d.state === 'LOCKED')
  const closed = c.against.shut_days.filter((d) => d.state === 'CLOSED')
  const open = !c.resolution

  return (
    <>
      <div className={pa.drawerBody} data-testid="conflict-detail">
        <button type="button" className={pa.ghostBtn} onClick={onBack}>
          ← All weighing conflicts
        </button>
        <div className={pa.lateTitleRow}>
          <b className={pa.lateTitle}>{c.title}</b>
          <span className={pa.lateTag} data-testid="conflict-status">
            {c.resolution ? decisionLabel(c.resolution.decision) : 'Open: waiting for a decision'}
          </span>
        </div>

        <div className={pa.lateH}>Why it was held</div>
        <ul className={pa.lateWages} data-testid="conflict-why">
          {c.reasons.map((r) => (
            <li key={r}>{REASON_TEXT[r] || r}</li>
          ))}
        </ul>

        <div className={pa.lateH}>The weighing from the device</div>
        <table className={pa.lateTable} data-testid="conflict-held">
          <tbody>
            <tr>
              <th scope="row">Weighed by</th>
              <td>
                {c.held.operator}
                {c.held.device_label ? ` · ${c.held.device_label}` : ''}
              </td>
            </tr>
            <tr>
              <th scope="row">Captured</th>
              <td>{stamp(c.held.captured_at)}</td>
            </tr>
            <tr>
              <th scope="row">Arrived in Control</th>
              <td>{stamp(c.held.arrived_at)}</td>
            </tr>
            <tr>
              <th scope="row">Scale reading (gross)</th>
              <td>{kg(c.held.gross_kg)}</td>
            </tr>
            <tr>
              <th scope="row">Empty bin weight (tare)</th>
              <td>
                {kg(c.held.tare_total_kg)}
                <span className={pa.lateHist}> {c.held.tare_source === 'device' ? 'recorded by the device; Control does not edit it' : c.held.tare_source}</span>
              </td>
            </tr>
            <tr>
              <th scope="row">Net</th>
              <td>
                <b>{kg(c.held.net_kg)}</b>
              </td>
            </tr>
          </tbody>
        </table>

        <div className={pa.lateH}>The bins it covers</div>
        <table className={pa.lateTable} data-testid="conflict-bins">
          <thead>
            <tr>
              <th>Bin</th>
              <th>Carries now</th>
              <th>Current source</th>
              <th>Its empty weight</th>
              <th>Would take from the new weighing</th>
            </tr>
          </thead>
          <tbody>
            {c.bins.map((b) => (
              <tr key={b.batch_id} data-testid="conflict-bin">
                <td>{b.bin}</td>
                <td>{b.current.weighed_kg === null ? 'Not weighed' : kg(b.current.weighed_kg)}</td>
                <td>{b.current.weighed_kg === null ? '—' : `${b.current.source || 'Unknown'}${b.current.by ? ` · ${b.current.by}` : ''}${b.current.at ? ` · ${stamp(b.current.at)}` : ''}`}</td>
                <td>{kg(b.tare_kg)}</td>
                <td>{b.held_share_kg === null ? 'No field estimate: no share' : <b>{kg(b.held_share_kg)}</b>}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {c.against.shut_days.length > 0 && (
          <div className={locked.length ? pa.lateBlocked : pa.lateInfo} data-testid="conflict-days">
            {c.against.shut_days.map((d) => `${dayLabel(d.date)}: ${d.state === 'LOCKED' ? 'locked' : 'closed'}`).join(' · ')}.{' '}
            {locked.length > 0
              ? 'Using the new weighing will be refused while a day is locked: unlock it first (unlocking recalculates that whole day). Keeping the current weight is still allowed.'
              : closed.length > 0
                ? 'A closed day takes a weighing, as it takes a Control weight, once a person has decided it.'
                : ''}
          </div>
        )}

        {c.resolution && (
          <div className={pa.lateInfo} data-testid="conflict-resolution">
            <b>{decisionLabel(c.resolution.decision)}</b> by {c.resolution.actor} on {stamp(c.resolution.decided_at)}. Reason: {c.resolution.reason}
            {c.resolution.decision === 'used_new' ? ' The bins now carry their share of the new weighing.' : ' The bins kept the weights they carried, and the held weighing stays on record.'}
          </div>
        )}
        {open && !canEdit && (
          <div className={pa.lateInfo} data-testid="conflict-readonly">
            You can read this weighing. Deciding it needs edit access to Batches.
          </div>
        )}
      </div>
      {open && canEdit && (
        <div className={o.drawerFoot}>
          <button type="button" className={o.ghost} onClick={() => setDeciding('kept_current')} data-testid="conflict-keep">
            Keep current weight
          </button>
          <button type="button" className={o.primary} onClick={() => setDeciding('used_new')} data-testid="conflict-use-new">
            Use new weighing
          </button>
        </div>
      )}
      {deciding && (
        <DecisionModal
          user={user}
          conflict={c}
          decision={deciding}
          onClose={() => setDeciding(null)}
          onBusy={onBusy}
          onDone={(message) => {
            setDeciding(null)
            load()
            onDecided(message)
          }}
        />
      )}
    </>
  )
}

function DecisionModal({
  user,
  conflict: c,
  decision,
  onClose,
  onBusy,
  onDone,
}: {
  user: Props['user']
  conflict: Conflict
  decision: 'kept_current' | 'used_new'
  onClose: () => void
  onBusy: (b: boolean) => void
  onDone: (message: string) => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [lockedDates, setLockedDates] = useState<string[]>([])
  const el = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { busy, dirty: !!reason.trim() && !busy, el, modal: true, focusFirst: true })
  const why = reason.trim()
  const keep = decision === 'kept_current'

  const submit = async () => {
    if (!why || busy) return
    setBusy(true)
    onBusy(true)
    setErr('')
    setLockedDates([])
    try {
      const r = await apiJson<{ already_resolved?: boolean; message?: string }>(user, `${CONFLICTS_PATH}/${c.id}/resolve`, { body: { decision, reason: why } })
      onDone(r.already_resolved ? r.message || 'Already decided' : keep ? 'Current weight kept' : 'New weighing used')
    } catch (e: any) {
      if (e?.body?.code === 'DAY_LOCKED') {
        setErr(CONFLICT_LOCKED_TEXT)
        setLockedDates(Array.isArray(e.body.locked_dates) ? e.body.locked_dates : [])
      } else {
        setErr(e?.message || 'It was not saved')
      }
    } finally {
      setBusy(false)
      onBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={el} className={[o.modal, o.wide].join(' ')} role="dialog" aria-label={keep ? 'Keep the current weight' : 'Use the new weighing'} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{keep ? 'Keep the current weight?' : 'Use the new weighing?'}</h2>
            <p className={o.sub} data-testid="conflict-consequence">
              {keep
                ? `The bins keep the weights they carry now. The held weighing (${kg(c.held.net_kg)} net, ${c.held.operator}) is set aside and stays on record, and nothing is recalculated. This can be decided even on a locked day. It is decided once.`
                : `Each bin takes its share of the new ${kg(c.held.net_kg)} net. Whatever a bin carries now (a Control weight or an earlier weighing) stops being its weight, and the cycle weights and credit in those bins are shared again. It is recorded with your name and reason, it is decided once, and it cannot be reversed from this screen.`}
            </p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={o.field}>
            <label className={o.fieldLabel}>Reason, kept with the decision{' '}<span className={o.req}>required</span></label>
            <input className={o.input} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} aria-label="Reason" />
          </div>
          {err && (
            <div className={o.danger} role="alert" data-testid="conflict-error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" style={{ flex: 'none', marginTop: '2px' }}>
                <circle cx="12" cy="12" r="9" /><path d="M12 8v4" /><path d="M12 16h.01" />
              </svg>
              <span><b>{err}</b>
              {lockedDates.length > 0 && <span> Locked: {lockedDates.map(dayLabel).join(', ')}.</span>}</span>
            </div>
          )}
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <PendingButton busy={busy} pending="Saving…" className={o.primary} onClick={submit} disabled={!why} data-testid="conflict-confirm">
            {keep ? 'Keep current weight' : 'Use new weighing'}
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}
